import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { useCreateBlog } from "../../hooks/blog-posts/useCreateBlog";
import { useUpdateBlog } from "../../hooks/blog-posts/useUpdateBlog";
import { useGetBlogCategories } from "../../hooks/blog-categories/useGetBlogCategories";
import { useGetBlogTags } from "../../hooks/blog-tags/useGetBlogTags";
import FormInput from "../inputs/FormInput";
import TextEditor from "../inputs/TextEditor";
import LanguageToggle from "../global/LanguageToggle";

const FormAddEditBlog = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const [titleAr, setTitleAr] = useState("");
    const [titleEn, setTitleEn] = useState("");
    const [descriptionAr, setDescriptionAr] = useState("");
    const [descriptionEn, setDescriptionEn] = useState("");
    const [contentAr, setContentAr] = useState("");
    const [contentEn, setContentEn] = useState("");
    const [categoryIds, setCategoryIds] = useState([]);
    const [tagIds, setTagIds] = useState([]);
    const [showInHome, setShowInHome] = useState(true);
    const [mainImage, setMainImage] = useState(null);
    const [gallery, setGallery] = useState([]);

    const { createBlogMutate, isPending: isCreating } = useCreateBlog();
    const { updateBlogMutate, isPending: isUpdating } = useUpdateBlog();
    const { categories } = useGetBlogCategories({ per_page: 1000 });
    const { tags } = useGetBlogTags({ per_page: 1000 });

    const loadingSubmit = isCreating || isUpdating;

    useEffect(() => {
        if (isEdit && currentData) {
            setTitleAr(currentData?.title?.ar || "");
            setTitleEn(currentData?.title?.en || "");
            setDescriptionAr(currentData?.description?.ar || "");
            setDescriptionEn(currentData?.description?.en || "");
            setContentAr(currentData?.content?.ar || "");
            setContentEn(currentData?.content?.en || "");
            setCategoryIds(currentData?.categories?.map(c => c.id) || []);
            setTagIds(currentData?.tags?.map(t => t.id) || []);
            setShowInHome(currentData?.show_in_home ?? true);
            setMainImage(currentData?.main_image || null);
            setGallery(currentData?.gallery || []);
        }
    }, [isEdit, currentData]);

    const handleImageChange = (e, setter) => {
        const val = e.target.value;
        setter(val);
    };

    const getImageName = (imageVal) => {
        if (!imageVal) return "";
        if (typeof imageVal === 'string') {
            return imageVal.substr(imageVal.lastIndexOf('/') + 1);
        }
        return imageVal;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const categoryIdsArray = Array.isArray(categoryIds) ? categoryIds : [categoryIds].filter(id => id);
        const tagIdsArray = Array.isArray(tagIds) ? tagIds : [tagIds].filter(id => id);

        const galleryItems = Array.isArray(gallery)
            ? gallery.filter(g => g != null).map(g => {
                if (typeof g === 'string') return g;
                if (g?.file_name) return g.file_name;
                if (g?.file?.name) return g.file.name;
                if (g?.name) return g.name;
                return g;
              })
            : [];

        const payload = {
            title: { ar: titleAr || titleEn, en: titleEn || titleAr },
            description: { ar: descriptionAr || descriptionEn, en: descriptionEn || descriptionAr },
            content: { ar: contentAr || contentEn, en: contentEn || contentAr },
            blog_category_ids: categoryIdsArray,
            tag_ids: tagIdsArray,
            show_in_home: showInHome,
            main_image: getImageName(mainImage),
            gallery: galleryItems,
        };

        if (isEdit) {
            updateBlogMutate({ id: currentData.id, data: payload });
        } else {
            createBlogMutate(payload);
        }

        if (onSubmitSuccess) onSubmitSuccess();
    };

    const categoryOptions = categories.map(cat => ({
        id: cat.id,
        name: isArabic ? (cat.title?.ar || cat.title?.en) : (cat.title?.en || cat.title?.ar),
    }));

    const tagOptions = tags.map(tag => ({
        id: tag.id,
        name: isArabic ? (tag.title?.ar || tag.title?.en) : (tag.title?.en || tag.title?.ar),
    }));

    return (
        <Paper sx={{ p: 3, mt: 2, direction: isArabic ? "rtl" : "ltr", backgroundColor: isDark ? "transparent" : "#FFFFFF" }}>
            <Box component="form" onSubmit={handleSubmit} sx={{ p: { xs: 2, md: 4 }, display: "flex", flexDirection: "column", gap: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6">
                        {isEdit ? (isArabic ? "تعديل المقال" : "Edit Blog Post") : (isArabic ? "إضافة مقال جديد" : "Add New Blog Post")}
                    </Typography>
                    <LanguageToggle activeLang={activeLang} onChange={(l) => setActiveLang(l)} showLabel={true} />
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <FormInput
                            label={isArabic ? "الصورة الرئيسية" : "Main Image"}
                            type="file"
                            name="main_image"
                            value={mainImage}
                            onChange={(e) => handleImageChange(e, setMainImage)}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormInput
                            label={isArabic ? "معرض الصور" : "Gallery"}
                            type="multi-image"
                            name="gallery"
                            value={gallery}
                            onChange={setGallery}
                            maxImages={20}
                        />
                    </Grid>

                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput label="عنوان المقال (عربي)" placeholder="أدخل عنوان المقال بالعربي" value={titleAr} onChange={(e) => setTitleAr(e.target.value)} required fullWidth />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput label="Blog Title (English)" placeholder="Enter blog title in English" value={titleEn} onChange={(e) => setTitleEn(e.target.value)} required fullWidth />
                        </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "التصنيفات" : "Categories"}
                            type="select"
                            multiple
                            name="category_ids"
                            value={categoryIds}
                            onChange={(e) => setCategoryIds(e.target.value)}
                            options={categoryOptions}
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الوسوم" : "Tags"}
                            type="select"
                            multiple
                            name="tag_ids"
                            value={tagIds}
                            onChange={(e) => setTagIds(e.target.value)}
                            options={tagOptions}
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "عرض في الرئيسية" : "Show in Home"}
                            type="switch"
                            name="show_in_home"
                            checked={showInHome}
                            onChange={(e) => setShowInHome(e.target.checked)}
                        />
                    </Grid>

                    {activeLang === "ar" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                الوصف (عربي)
                            </Typography>
                            <TextEditor
                                name="description_ar"
                                value={descriptionAr}
                                onChange={(e) => setDescriptionAr(e.target.value)}
                                placeholder="أدخل الوصف بالعربي"
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                Description (English)
                            </Typography>
                            <TextEditor
                                name="description_en"
                                value={descriptionEn}
                                onChange={(e) => setDescriptionEn(e.target.value)}
                                placeholder="Enter description in English"
                            />
                        </Grid>
                    )}

                    {activeLang === "ar" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                المحتوى (عربي)
                            </Typography>
                            <TextEditor
                                name="content_ar"
                                value={contentAr}
                                onChange={(e) => setContentAr(e.target.value)}
                                placeholder="أدخل المحتوى بالعربي"
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                Content (English)
                            </Typography>
                            <TextEditor
                                name="content_en"
                                value={contentEn}
                                onChange={(e) => setContentEn(e.target.value)}
                                placeholder="Enter content in English"
                            />
                        </Grid>
                    )}
                </Grid>

                <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                    <Button type="submit" variant="contained" color="primary" disabled={loadingSubmit} sx={{ minWidth: 120 }}>
                        {loadingSubmit ? (isArabic ? "جارٍ الحفظ..." : "Saving...") : (isEdit ? (isArabic ? "تحديث" : "Update") : (isArabic ? "حفظ" : "Save"))}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditBlog;
